import { Controller } from "@hotwired/stimulus"

// tasks--dependency-graph-launcher — the board's single dependency-graph
// launcher (MC-1606).
//
// Rides on the board root so both launch points are in its scope: the card's
// dependency mark and the drawer's "View graph" control both fire `#open`
// with the task's graph frame id, src and label as action params. The modal
// it drives is rendered once at board level
// (`Tasks::DependencyGraphLauncherComponent`) and reached through a
// `core--modal` Stimulus outlet — the idiomatic way to drive one controller
// from another (see `top-nav--search-hotkey`).
//
// The point of centralising here: the modal's markup lives outside every
// card, so a click inside it (backdrop, panel, or a card the graph draws) has
// no card to bubble to. MC-1462's drawer-open leak is gone by construction,
// not by a propagation guard.
//
// Lazy (MC-992): the modal body starts empty; `#open` injects the clicked
// task's `loading: "lazy"` graph frame only when the graph is opened, so no
// traversal runs until then. The frame is rebuilt from the clicked task's
// params on every open, so the launcher always shows the graph that was asked
// for and never a stale one.
//
// All state is DOM-derived (the modal owns open/closed via its own
// controller; the frame is a live child of the frame target), so a Turbo
// navigation that repaints the board leaves nothing to reconcile — connect()
// binds no listeners and disconnect() has nothing to tear down.
export default class extends Controller {
  static outlets = ["core--modal"]
  static targets = ["frame"]

  open(event) {
    event?.preventDefault()
    if (!this.hasCoreModalOutlet || !this.hasFrameTarget) return

    const params = event?.params || {}
    const { frame, src, label } = params
    if (!frame || !src) return

    this.renderFrame(frame, src)
    if (label) this.setTitle(label)
    this.coreModalOutlet.open()
  }

  // Replace the modal body with a fresh lazy turbo-frame for this task.
  // Rebuilding on every open keeps the graph in step with the clicked task
  // and preserves the lazy contract — the frame (and the fetch it triggers)
  // exists only while a graph is open. The frame id matches the endpoint's
  // own response frame (`Tasks::DependencyGraphComponent.frame_id`), so Turbo
  // swaps the traversal's HTML in when it lands.
  renderFrame(frameId, src) {
    const frame = document.createElement("turbo-frame")
    frame.id = frameId
    frame.setAttribute("src", src)
    frame.setAttribute("loading", "lazy")

    const loading = document.createElement("div")
    loading.className =
      "flex items-center justify-center py-12 text-sm text-muted"
    loading.setAttribute("role", "status")
    loading.setAttribute("aria-busy", "true")
    loading.textContent = "Loading dependency graph…"
    frame.appendChild(loading)

    this.frameTarget.replaceChildren(frame)
  }

  // Name the generic modal for the task the graph was opened from. The
  // heading is the modal's `aria-labelledby` target, so updating its text
  // keeps the dialog's accessible name in step with what is shown.
  setTitle(label) {
    const title = this.coreModalOutletElement.querySelector(".modal__title")
    if (title) title.textContent = label
  }
}
